import { Component, HostBinding, HostListener, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { environment } from 'environments/environment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AuthenticationService } from 'app/server/services';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { CoreConfigService } from '@core/services/config.service';
import { CoreMediaService } from '@core/services/media.service';
import { ProfilePictureService } from '@core/services/profile-picture.service';

import { User } from 'app/server/models';
import { Router } from '@angular/router';
import { UniversityService } from '../../../server/services/university.service';
import { University } from '../../../server/models/university/university';
import { SchoolUniversityService } from '../../../server/services/school-university.service';
import { SchoolUniversity } from '../../../server/models/school/school-university';
import { CoreMenuService } from '@core/components/core-menu/core-menu.service';
import { TranslateService } from '@ngx-translate/core';
import { CurrentUniversityService } from '../../../server/services/current-university.service';
import { CurrentSchoolUniversityService } from '../../../server/services/current-school-university.service';
import { ProfileUser } from '../../../server/models/profile/profile';
import { ProfileType } from '../../../server/enums/profile-type';
import { LayoutType } from '../../../server/enums/layout-type';
import { universityMenu } from '../../../menu/university';
import { CoreMenu } from '@core/types';
import { menu } from '../../../menu/menu';
import { CurrentCareerService } from '../../../server/services/current-career.service';
import { CareerService } from '../../../server/services/career.service';
import { Career } from '../../../server/models/career/career';
import { APP_LANGUAGES, AppLanguage } from 'app/config/languages.config';
import { VocabularyService } from 'app/server/services/vocabulary.service';

@Component({
	selector: 'app-navbar',
	templateUrl: './navbar.component.html',
	styleUrls: ['./navbar.component.scss'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class NavbarComponent implements OnInit, OnDestroy {
	public horizontalMenu: boolean;
	public hiddenMenu: boolean;

	public coreConfig: any;
	public currentSkin: string;

	public currentUser: User;
	public currentUniversity: University;
	public currentSchool: SchoolUniversity;

	public languageOptions: any;

	/** Only the languages the backend actually has vocabulary data for (single source of truth). */
	get appLanguages(): AppLanguage[] {
		return APP_LANGUAGES.filter(l => this._vocabularyService.supportedLangCodes.includes(l.code));
	}

	/** Language currently in use, shown on the user menu toggle. */
	get activeLanguage(): AppLanguage | undefined {
		const code = this._translateService.currentLang || this._translateService.defaultLang;
		return APP_LANGUAGES.find(l => l.code === code);
	}

	public navigation: any;
	public selectedLanguage: any;
	user: User;
	menu: any;
	activeProfile: ProfileUser;
	universityMenu: Array<CoreMenu> = [];
	menuMultitenant: Array<CoreMenu> = [];
	careers: Array<Career>;
	beUrl: string;

	@HostBinding('class.fixed-top')
	public isFixed = false;

	@HostBinding('class.navbar-static-style-on-scroll')
	public windowScrolled = false;

	// Add .navbar-static-style-on-scroll on scroll using HostListener & HostBinding
	@HostListener('window:scroll', [])
	onWindowScroll() {
		if (
			(window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop > 100) &&
			this.coreConfig.layout.navbar.type == 'navbar-static-top' &&
			this.coreConfig.layout.type == 'horizontal'
		) {
			this.windowScrolled = true;
		} else if ((this.windowScrolled && window.pageYOffset) || document.documentElement.scrollTop || document.body.scrollTop < 10) {
			this.windowScrolled = false;
		}
	}

	// Private
	private _unsubscribeAll: Subject<any>;

	constructor(
		private _router: Router,
		public _authenticationService: AuthenticationService,
		private _coreMenuService: CoreMenuService,
		private _coreConfigService: CoreConfigService,
		private _coreMediaService: CoreMediaService,
		private _coreSidebarService: CoreSidebarService,
		public _currentUniversityService: CurrentUniversityService,
		private _universityService: UniversityService,
		private _schoolUniversityService: SchoolUniversityService,
		public _currentSchoolUniversityService: CurrentSchoolUniversityService,
		public _translateService: TranslateService,
		public _currentCareerActive: CurrentCareerService,
		public _careerService: CareerService,
		private _profilePictureService: ProfilePictureService,
		private _vocabularyService: VocabularyService
	) {
		this._authenticationService.currentUser.subscribe(x => (this.currentUser = x));

		// Set the private defaults
		this._unsubscribeAll = new Subject();

		this.universityMenu = JSON.parse(JSON.stringify(universityMenu));
		this.menuMultitenant = JSON.parse(JSON.stringify(menu));
	}

	// Public Methods
	// -----------------------------------------------------------------------------------------------------

	/**
	 * Toggle sidebar open
	 *
	 * @param key
	 */
	toggleSidebar(key): void {
		this._coreSidebarService.getSidebarRegistry(key).toggleOpen();
	}

	translate(lang: string) {
		this._translateService.use(lang);
		localStorage.setItem('language', lang);
	}

	/**
	 * Logout method
	 */
	logout() {
		this._authenticationService.logout(); /*.subscribe({
			next: res => {
				if (res) {
					this._router.navigate(['/authentication/login']);
				}
			},
		});*/
	}

	navigateToProfile(): void {
		this._router.navigate(['/profile']);
	}

	getProfilePictureUrl(user: User): string {
		return this._profilePictureService.getProfilePictureUrl(user?.profile_picture);
	}

	getUserInitials(user: User): string {
		const firstInitial = user?.name?.charAt(0) || '';
		const secondInitial = user?.surname?.charAt(0) || '';
		return `${firstInitial}${secondInitial}`.toUpperCase() || 'U';
	}

	cambiaProfilo(event: boolean) {
		if (this._currentSchoolUniversityService.currentSchoolSubject.value) {
			this._currentSchoolUniversityService.exitSchool();
		}

		if (this._currentCareerActive.currentCareerId.value) {
			this._currentCareerActive.exitActiveCareer();
		}

		this._authenticationService.exitActiveUser();

		this._coreConfigService.config = {
			layout: {
				navbar: {
					hidden: true,
				},
				menu: {
					hidden: true,
				},
				footer: {
					hidden: true,
				},
				customizer: false,
				enableLocalStorage: false,
			},
		};
		this._router.navigate(['authentication/login']);
		// window.location.href = 'authentication/login';
	}

	redirectToHome() {
		// Il logo porta alla home del livello in cui si è: multi-tenant per il SuperAdmin (uscendo da scuola e ateneo,
		// altrimenti MultitenantGuard rimanda a "Pagina non autorizzata", #19776), ateneo o scuola per gli altri profili.
		const profileType = this._authenticationService.currentActiveProfile.value?.profile?.profile_type_id;
		if (profileType === ProfileType.Multitenant) {
			this._currentSchoolUniversityService.exitSchool();
			this._currentUniversityService.exitUniversity();
			this._router.navigate(['/']);
		} else if (this._currentSchoolUniversityService.currentSchoolId.value) {
			this._router.navigate(['/university/school/home']);
		} else {
			this._router.navigate(['/university/home']);
		}
	}

	exitSchool() {
		this._currentSchoolUniversityService.exitSchool();

		// this.universityMenu = this._authenticationService.filterItems(this.universityMenu, this._authenticationService.activeProfileId.value);
		this.universityMenu = this._authenticationService.filterCombinedItems(
			this.universityMenu,
			this._authenticationService.currentActiveProfile.value
		);
		this._coreMenuService.register('university', this.universityMenu);
		this._coreMenuService.setCurrentMenu('university');

		this._router.navigate(['./university/home']);
	}

	exitUniversity() {
		this._currentUniversityService.exitUniversity();

		// this.menuMultitenant = this._authenticationService.filterItems(this.menuMultitenant, this._authenticationService.activeProfileId.value)
		this.menuMultitenant = this._authenticationService.filterCombinedItems(
			this.menuMultitenant,
			this._authenticationService.currentActiveProfile.value
		);
		this._coreMenuService.register('main', this.menuMultitenant);
		this._coreMenuService.setCurrentMenu('main');

		this._router.navigate(['./home']);
	}

	// Lifecycle Hooks
	// -----------------------------------------------------------------------------------------------------

	/**
	 * On init
	 */
	ngOnInit(): void {
		this.beUrl = environment.baseUrl;
		//riprende tutte le carriere per far apparire il cambio profilo
		this._careerService.careersFilteredBySchoolAndAuthUser.pipe(takeUntil(this._unsubscribeAll)).subscribe(res => {
			this.careers = res;
		});

		// Subscribe to the config changes
		this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
			this.coreConfig = config;
			this.horizontalMenu = config.layout.type === 'horizontal';
			this.hiddenMenu = config.layout.menu.hidden === true;
			this.currentSkin = config.layout.skin;

			// Fix: for vertical layout if default navbar fixed-top than set isFixed = true
			if (this.coreConfig.layout.type === 'vertical') {
				this._coreMediaService.onMediaUpdate.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
					const isCollapsible = window.matchMedia('(min-width: 1200px)');

					if (isCollapsible.matches) {
						this.isFixed = true;
					} else {
						this.isFixed = false;
					}
				});
			}
		});

		// Horizontal Layout Only: Add class fixed-top to navbar below large screen
		if (this.coreConfig.layout.type == 'horizontal') {
			this._coreMediaService.onMediaUpdate.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
				this._coreMediaService.onMediaUpdate.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
					const isCollapsible = window.matchMedia('(min-width: 1200px)');

					if (isCollapsible.matches) {
						this.isFixed = false;
					} else {
						this.isFixed = true;
					}
				});
			});
		}

		this._coreMenuService.onMenuRegistered.subscribe(res => {
			if (res) {
				this.menu = res[0];
			}
		});

		this._currentUniversityService.currentUniversitySubject.pipe(takeUntil(this._unsubscribeAll)).subscribe(res => {
			this.currentUniversity = res;
		});

		this._currentSchoolUniversityService.currentSchoolSubject.pipe(takeUntil(this._unsubscribeAll)).subscribe(res => {
			this.currentSchool = res;
		});

		this.activeProfile = this._authenticationService.currentActiveProfile.value;
	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		// Unsubscribe from all subscriptions
		this._unsubscribeAll.next(null);
		this._unsubscribeAll.complete();
	}

	protected readonly ProfileType = ProfileType;
	protected readonly LayoutType = LayoutType;
}
